import { useQuery } from '@tanstack/react-query';
import { Activity, CircleCheck, ShieldAlert, Timer, TriangleAlert } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Cifra } from '@/modules/inicio/piezas';

interface Aviso {
  pasarela: string;
  referencia: string | null;
  autentico: boolean;
  ip: string | null;
  detalle: string | null;
  recibidoEn: string;
}

interface Pasada {
  pasadaEn: string;

  mirados: number;
  resueltos: number;
  detalle: string | null;
}

interface PagoDeLaSonda {
  referencia: string;
  pasarela: string;
  estado: string;
  total: number;
  autorizacion: string | null;
  iniciadoEn: string;
  actualizadoEn: string;
}

interface Vigilancia {
  avisos: Aviso[];
  avisosHoy: number;
  sinFirmaHoy: number;

  ultimoAviso: string | null;
  sonda: {
    pasadaEn: string | null;
    resueltos: number;
    pendientes: number;
    detalle: string | null;
  } | null;
  pasadas: Pasada[];
  resueltosPorLaSonda: PagoDeLaSonda[];
}

const HORAS_DE_SILENCIO = 24;

export function PaginaDeMonitoreo() {
  const vigilancia = useQuery({
    queryKey: ['pagos-en-linea', 'monitoreo'],
    queryFn: () => api.get<Vigilancia>('/pagos-en-linea/monitoreo', { query: { avisos: 50 } }),

    refetchInterval: 30_000,
  });

  const v = vigilancia.data;
  const callado = silencio(v?.ultimoAviso ?? null);

  return (
    <>
      <EncabezadoDePagina
        icono={Activity}
        color="indigo"
        titulo="Monitoreo de pagos en línea"
        subtitulo="Los avisos de las pasarelas y la sonda de la ventanilla"
      />

      {vigilancia.isPending ? (
        <Cargando titulo="Cargando" detalle="Tomando el pulso" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Cifra rotulo="Avisos hoy" valor={String(v?.avisosHoy ?? 0)} icono={Activity} tono="azul" />

            <Cifra
              rotulo="Sin firma válida"
              valor={String(v?.sinFirmaHoy ?? 0)}
              icono={ShieldAlert}
              tono={(v?.sinFirmaHoy ?? 0) > 0 ? 'rojo' : 'pizarra'}
            />

            <Cifra
              rotulo="Cerrados en la última pasada"
              valor={String(v?.sonda?.resueltos ?? 0)}
              icono={CircleCheck}
            />
            <Cifra
              rotulo="Pagos sin resolver"
              valor={String(v?.sonda?.pendientes ?? 0)}
              icono={Timer}
              tono={(v?.sonda?.pendientes ?? 0) > 0 ? 'ambar' : 'pizarra'}
            />
          </div>

          {callado && (
            <p className="mt-5 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                <span className="font-bold">
                  {v?.ultimoAviso
                    ? `Sin avisos desde hace más de ${HORAS_DE_SILENCIO} horas.`
                    : 'Nunca ha llegado un aviso de ninguna pasarela.'}
                </span>{' '}
                Revise que la ventanilla tenga configurada su dirección pública de avisos y que
                esa dirección sea alcanzable desde fuera.
              </span>
            </p>
          )}

          <Seccion titulo="La sonda">
            <dl className="grid gap-x-6 gap-y-2 p-4 text-[11px] uppercase sm:grid-cols-2">
              <Dato
                rotulo="Última pasada"
                valor={v?.sonda?.pasadaEn ? enFechaYHora(v.sonda.pasadaEn) : 'Nunca'}
              />
              <Dato rotulo="Detalle" valor={v?.sonda?.detalle ?? '—'} />
            </dl>
          </Seccion>

          <Seccion titulo={`Pagos que cerró la sonda (${v?.resueltosPorLaSonda.length ?? 0})`}>
            {(v?.resueltosPorLaSonda.length ?? 0) === 0 ? (
              <p className="p-6 text-center text-[11px] uppercase text-armazon-400">
                La sonda todavía no ha tenido que cerrar ningún pago
              </p>
            ) : (
              <Tabla columnas={['Referencia', 'Iniciado', 'Cerrado', 'Tardó', 'Estado', 'Autorización', 'Importe']}>
                {(v?.resueltosPorLaSonda ?? []).map((p) => (
                  <tr key={p.referencia} className="hover:bg-armazon-50/60">
                    <td className="importe px-4 py-2.5 text-xs font-semibold text-armazon-800">
                      {p.referencia}
                    </td>
                    <td className="importe px-4 py-2.5 text-xs text-armazon-600">
                      {enFechaYHora(p.iniciadoEn)}
                    </td>
                    <td className="importe px-4 py-2.5 text-xs text-armazon-600">
                      {enFechaYHora(p.actualizadoEn)}
                    </td>
                    <td className="importe px-4 py-2.5 text-xs text-armazon-700">
                      {loQueTardo(p.iniciadoEn, p.actualizadoEn)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Etiqueta estado={p.estado} />
                    </td>
                    <td className="importe px-4 py-2.5 text-xs text-armazon-600">
                      {p.autorizacion ?? '—'}
                    </td>
                    <td className="importe px-4 py-2.5 text-right text-xs font-semibold text-armazon-800">
                      {enDolares(p.total)}
                    </td>
                  </tr>
                ))}
              </Tabla>
            )}
          </Seccion>

          <Seccion titulo={`Últimas pasadas con trabajo (${v?.pasadas.length ?? 0})`}>
            {(v?.pasadas.length ?? 0) === 0 ? (
              <p className="p-6 text-center text-[11px] uppercase text-armazon-400">
                Ninguna pasada ha encontrado pagos pendientes todavía
              </p>
            ) : (
              <Tabla columnas={['Pasada', 'Miró', 'Cerró', 'Detalle']}>
                {(v?.pasadas ?? []).map((p, i) => (
                  <tr key={`${p.pasadaEn}-${i}`} className="hover:bg-armazon-50/60">
                    <td className="importe px-4 py-2.5 text-xs text-armazon-700">
                      {enFechaYHora(p.pasadaEn)}
                    </td>
                    <td className="importe px-4 py-2.5 text-xs text-armazon-600">{p.mirados}</td>
                    <td className="importe px-4 py-2.5 text-xs font-semibold text-armazon-800">
                      {p.resueltos}
                    </td>
                    <td className="px-4 py-2.5 text-[11px] uppercase text-armazon-500">
                      {p.detalle ?? '—'}
                    </td>
                  </tr>
                ))}
              </Tabla>
            )}
          </Seccion>

          <Seccion titulo={`Últimos avisos (${v?.avisos.length ?? 0})`}>
            {(v?.avisos.length ?? 0) === 0 ? (
              <p className="p-6 text-center text-[11px] uppercase text-armazon-400">
                Todavía no se ha recibido ningún aviso
              </p>
            ) : (
              <Tabla columnas={['Recibido', 'Pasarela', 'Referencia', 'Firma', 'Origen']}>
                {(v?.avisos ?? []).map((a, i) => (
                  <tr key={`${a.recibidoEn}-${i}`} className="hover:bg-armazon-50/60">
                    <td className="importe px-4 py-2.5 text-xs text-armazon-700">
                      {enFechaYHora(a.recibidoEn)}
                    </td>
                    <td className="px-4 py-2.5 text-xs uppercase text-armazon-700">{a.pasarela}</td>
                    <td className="importe px-4 py-2.5 text-xs text-armazon-600">
                      {a.referencia ?? '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${
                          a.autentico ? 'bg-marca-50 text-marca-700' : 'bg-red-50 text-alerta'
                        }`}
                      >
                        {a.autentico ? 'Válida' : 'No válida'}
                      </span>
                    </td>
                    <td className="importe px-4 py-2.5 text-[11px] text-armazon-500">{a.ip ?? '—'}</td>
                  </tr>
                ))}
              </Tabla>
            )}
          </Seccion>
        </>
      )}
    </>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mt-6 overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
      <h2 className="border-b border-armazon-200 bg-armazon-50/70 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-armazon-600">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function Tabla({ columnas, children }: { columnas: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-armazon-50/80">
          <tr>
            {columnas.map((c) => (
              <th
                key={c}
                scope="col"
                className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-armazon-500"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-armazon-100">{children}</tbody>
      </table>
    </div>
  );
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 font-semibold text-armazon-500">{rotulo}:</dt>
      <dd className="min-w-0 truncate text-armazon-800">{valor}</dd>
    </div>
  );
}

function Etiqueta({ estado }: { estado: string }) {
  const tono =
    estado === 'APLICADO'
      ? 'bg-marca-50 text-marca-700'
      : estado === 'RECHAZADO' || estado === 'POR_REVISAR'
        ? 'bg-red-50 text-alerta'
        : 'bg-armazon-100 text-armazon-600';
  return (
    <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${tono}`}>
      {estado.replace('_', ' ')}
    </span>
  );
}

function enFechaYHora(iso: string): string {
  const fecha = new Date(iso);
  return Number.isNaN(fecha.getTime())
    ? iso
    : fecha.toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'medium' });
}

function enDolares(valor: number): string {
  return valor.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function loQueTardo(desde: string, hasta: string): string {
  const segundos = Math.round((new Date(hasta).getTime() - new Date(desde).getTime()) / 1000);
  if (!Number.isFinite(segundos) || segundos < 0) {
    return '—';
  }
  if (segundos < 60) {
    return `${segundos} s`;
  }
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) {
    return `${minutos} min ${segundos % 60} s`;
  }
  return `${Math.floor(minutos / 60)} h ${minutos % 60} min`;
}

function silencio(ultimo: string | null): boolean {
  if (ultimo === null) {
    return true;
  }
  const cuando = new Date(ultimo).getTime();
  return Number.isNaN(cuando) || Date.now() - cuando > HORAS_DE_SILENCIO * 3600_000;
}
